import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { RendezVous } from '../../types';
import { useRendezVous } from '../../hooks/useRendezVous';
import { communicationService } from '../../services/api';
import { 
  Plus, 
  List, 
  Calendar as CalendarIcon, 
  Settings, 
  Filter,
  Eye,
  EyeOff,
  Send,
  CalendarCheck
} from 'lucide-react';
import CalendarView from './CalendarView';
import RendezVousForm from './RendezVousForm';
import RendezVousDetails from './RendezVousDetails';
import CalendrierSync from './CalendrierSync';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

type ViewMode = 'calendar' | 'list';
type CalendarView = 'month' | 'week' | 'day';

export default function PlanningDashboard() {
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showForm, setShowForm] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState<RendezVous | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCalendrierSync, setShowCalendrierSync] = useState(false);
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Calculer la plage de dates selon la vue
  const getDateRange = () => {
    if (calendarView === 'month') {
      return {
        start: format(startOfMonth(selectedDate), 'yyyy-MM-dd'),
        end: format(endOfMonth(selectedDate), 'yyyy-MM-dd')
      };
    } else if (calendarView === 'week') {
      return {
        start: format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    } else {
      return {
        start: format(selectedDate, 'yyyy-MM-dd'),
        end: format(selectedDate, 'yyyy-MM-dd')
      };
    }
  };

  const dateRange = getDateRange();

  // Utiliser le hook personnalisé pour les rendez-vous
  const {
    filteredRendezVous,
    isLoading,
    changerStatutRdv
  } = useRendezVous({ dateRange, statusFilter });

  // Détecter navigation depuis d'autres composants
  useEffect(() => {
    const navigationState = location.state as {
      selectedPatientId?: number;
      patientInfo?: any;
      targetDate?: string;
    } | null;

    if (navigationState?.targetDate) {
      setSelectedDate(new Date(navigationState.targetDate));
      setCalendarView('month');
      window.history.replaceState({}, document.title);
    } else if (navigationState?.selectedPatientId) {
      setShowForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleRendezVousClick = (rdv: RendezVous) => {
    setSelectedRdv(rdv);
    setShowDetails(true);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleCreateConsultation = (rdv: RendezVous) => {
    navigate('/consultations', {
      state: {
        patientId: rdv.patient,
        doctorId: rdv.doctor,
        rendezVousId: rdv.id,
        motif: rdv.motif,
        patientInfo: {
          nom: rdv.patient_details?.nom,
          prenoms: rdv.patient_details?.prenoms,
          telephone: rdv.patient_details?.telephone
        }
      }
    });
  };

  const handleSendReminders = async () => {
    try {
      const response = await communicationService.sendAppointmentReminders();
      alert(response.data.message);
    } catch (error) {
      console.error('Erreur envoi rappels:', error);
      alert('Erreur lors de l\'envoi des rappels');
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'attente', label: 'En attente' },
    { value: 'confirme', label: 'Confirmés' },
    { value: 'realise', label: 'Réalisés' },
    { value: 'annule', label: 'Annulés' },
    { value: 'non_honore', label: 'Non honorés' }
  ];

  if (showForm) {
    return (
      <RendezVousForm
        rendezVous={selectedRdv || undefined}
        onClose={() => {
          setShowForm(false);
          setSelectedRdv(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['rendez-vous'] });
          setShowForm(false);
          setSelectedRdv(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planning & Agenda</h1>
          <p className="text-sm text-gray-600 mt-1">
            Gérez vos rendez-vous et planifiez votre emploi du temps
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Filtre par statut */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              aria-label="Filtrer par statut"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-xl px-3 py-1.5 text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Synchronisation avec Google Agenda / Outlook */}
          <button
            onClick={() => setShowCalendrierSync(true)}
            className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
            title="Recevoir mes rendez-vous dans Google Agenda ou Outlook"
          >
            <CalendarCheck className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Synchroniser</span>
          </button>

          {/* Bouton d'envoi de rappels */}
          <button
            onClick={handleSendReminders}
            className="flex items-center px-3 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition-colors"
            title="Envoyer les rappels pour demain"
          >
            <Send className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Rappels</span>
          </button>

          {/* Toggle vue */}
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-2 text-sm font-medium flex items-center space-x-2 ${
                viewMode === 'calendar'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <CalendarIcon className="h-4 w-4" />
              <span>Calendrier</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium flex items-center space-x-2 ${
                viewMode === 'list'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="h-4 w-4" />
              <span>Liste</span>
            </button>
          </div>

          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau RDV
          </button>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total RDV</p>
              <p className="text-lg font-semibold text-gray-900">{filteredRendezVous.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Confirmés</p>
              <p className="text-lg font-semibold text-gray-900">
                {filteredRendezVous.filter(rdv => rdv.status === 'confirme').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">En attente</p>
              <p className="text-lg font-semibold text-gray-900">
                {filteredRendezVous.filter(rdv => rdv.status === 'attente').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Annulés</p>
              <p className="text-lg font-semibold text-gray-900">
                {filteredRendezVous.filter(rdv => rdv.status === 'annule').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      {isLoading ? (
        <div className="son-panel p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-500 mt-4">Chargement des rendez-vous...</p>
        </div>
      ) : viewMode === 'calendar' ? (
        <CalendarView
          rendezVous={filteredRendezVous}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          onRendezVousClick={handleRendezVousClick}
          onStatusChange={changerStatutRdv}
          view={calendarView}
          onViewChange={setCalendarView}
        />
      ) : (
        <div className="son-panel">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">
              Liste des rendez-vous
            </h2>
          </div>
          
          {filteredRendezVous.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Aucun rendez-vous trouvé pour les critères sélectionnés</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredRendezVous.map(rdv => (
                <div key={rdv.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <div className="text-sm text-gray-600">
                          {format(new Date(rdv.date_heure), 'dd/MM/yyyy HH:mm')}
                        </div>
                        <div className={`px-2 py-1 text-xs font-medium rounded-full border ${
                          rdv.status === 'confirme' ? 'bg-green-100 text-green-800 border-green-200' :
                          rdv.status === 'attente' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          rdv.status === 'annule' ? 'bg-red-100 text-red-800 border-red-200' :
                          'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {rdv.status}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 mb-2">
                        <span className="font-medium text-gray-900">
                          {rdv.patient_details?.nom} {rdv.patient_details?.prenoms}
                        </span>
                        <span className="text-sm text-gray-600">
                          {rdv.doctor_details?.user_details 
                            ? `Dr. ${rdv.doctor_details.user_details.first_name} ${rdv.doctor_details.user_details.last_name}`
                            : 'Médecin non assigné'
                          }
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <strong>Motif:</strong> {rdv.motif}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRendezVousClick(rdv)}
                        className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal détails */}
      {showDetails && selectedRdv && (
        <RendezVousDetails
          rendezVous={selectedRdv}
          onClose={() => {
            setShowDetails(false);
            setSelectedRdv(null);
          }}
          onEdit={() => {
            setShowDetails(false);
            setShowForm(true);
          }}
          onCreateConsultation={handleCreateConsultation}
          onStatusChange={changerStatutRdv}
        />
      )}

      {/* Synchronisation Google Agenda / Outlook */}
      {showCalendrierSync && (
        <CalendrierSync onClose={() => setShowCalendrierSync(false)} />
      )}
    </div>
  );
}